import React from 'react';
import { Link } from 'react-router-dom';
import './Auth.css';

const TermsAndConditions: React.FC = () => {
  return (
    <div className="auth-container">
      <div className="auth-card" style={{ maxWidth: '800px', textAlign: 'left' }}>
        <div className="auth-header">
          <h1>🦌 culr.app</h1>
          <h2>Terms & Conditions</h2>
        </div>

        <div style={{ padding: '20px 0', lineHeight: '1.6' }}>
          <p><strong>Last updated:</strong> {new Date().toLocaleDateString()}</p>
          <p><strong>Effective Date:</strong> {new Date().toLocaleDateString()}</p>

          <h3>1. About These Terms</h3>
          <p>
            These Terms and Conditions ("Terms") govern your use of culr.app ("the Service"), a deer cull tracking 
            and management platform operated in the United Kingdom. By accessing or using our Service, you agree 
            to be bound by these Terms and our Privacy Policy.
          </p>
          <p>
            If you do not agree to these Terms, please do not use our Service.
          </p>

          <h3>2. The Service</h3>
          <p>
            culr.app provides digital tools for recording, tracking, and managing deer cull activities in England. 
            The Service includes data entry forms, interactive mapping, data export functionality, and user authentication.
          </p>

          <h3>3. User Accounts and Registration</h3>
          <p>
            To use certain features of the Service, you must create an account using Google Authentication. 
            You are responsible for:
          </p>
          <ul>
            <li>Maintaining the security of your account credentials</li>
            <li>All activities that occur under your account</li>
            <li>Providing accurate and current information</li>
            <li>Notifying us immediately of any unauthorized use</li>
          </ul>

          <h3>4. Acceptable Use</h3>
          <p>You agree to use the Service only for lawful purposes and in accordance with these Terms. You must not:</p>
          <ul>
            <li>Violate any applicable UK laws or regulations, including wildlife protection laws</li>
            <li>Upload false, misleading, or inaccurate cull data</li>
            <li>Attempt to gain unauthorized access to the Service or other users' accounts</li>
            <li>Use the Service for any commercial purpose without our written consent</li>
            <li>Interfere with or disrupt the Service or servers</li>
            <li>Use automated systems to access the Service without permission</li>
          </ul>

          <h3>5. Wildlife Law Compliance</h3>
          <p>
            Users must comply with all applicable UK wildlife legislation, including but not limited to:
          </p>
          <ul>
            <li>The Wildlife and Countryside Act 1981</li>
            <li>The Deer Act 1991</li>
            <li>Local authority regulations and licensing requirements</li>
            <li>Firearms licensing and safety regulations</li>
          </ul>
          <p>
            culr.app is a record-keeping tool only and does not grant permission for deer culling activities.
          </p>

          <h3>6. Data Protection and Privacy (GDPR Compliance)</h3>
          <p>
            We process your personal data in accordance with UK GDPR and the Data Protection Act 2018. 
            Your data protection rights include:
          </p>
          <ul>
            <li><strong>Right of access:</strong> Request copies of your personal data</li>
            <li><strong>Right to rectification:</strong> Request correction of inaccurate data</li>
            <li><strong>Right to erasure:</strong> Request deletion of your personal data</li>
            <li><strong>Right to restrict processing:</strong> Request limitation of data processing</li>
            <li><strong>Right to data portability:</strong> Request transfer of your data</li>
            <li><strong>Right to object:</strong> Object to processing of your personal data</li>
          </ul>
          <p>
            <strong>Data Controller:</strong> culr.app<br/>
            <strong>Legal basis for processing:</strong> Legitimate interests (wildlife management and conservation)<br/>
            <strong>Data retention:</strong> Personal data is retained for 7 years or until account deletion<br/>
            <strong>International transfers:</strong> Data may be processed by Google services (covered by adequacy decisions)
          </p>

          <h3>7. Cookies and Tracking</h3>
          <p>
            We use essential cookies for authentication and functionality. Optional analytics cookies require your consent. 
            You can manage cookie preferences through our cookie banner or browser settings.
          </p>

          <h3>8. Consumer Rights (UK)</h3>
          <p>
            As a UK consumer, you have statutory rights under the Consumer Rights Act 2015. These Terms do not 
            affect your statutory rights. If you are dissatisfied with the Service, you may be entitled to:
          </p>
          <ul>
            <li>A refund (if applicable)</li>
            <li>Repair or replacement of faulty services</li>
            <li>Compensation for damages or losses</li>
          </ul>

          <h3>9. Intellectual Property</h3>
          <p>
            The Service and its content are protected by copyright, trademark, and other intellectual property laws. 
            You retain ownership of your cull data but grant us a license to process it for Service provision.
          </p>

          <h3>10. Limitation of Liability</h3>
          <p>
            To the fullest extent permitted by UK law, we exclude liability for:
          </p>
          <ul>
            <li>Indirect, special, or consequential damages</li>
            <li>Loss of profits, data, or business opportunities</li>
            <li>Decisions made based on data entered into the system</li>
          </ul>
          <p>
            Our total liability is limited to £100 or the amount paid for the Service (if any), whichever is greater. 
            This does not limit liability for death, personal injury, fraud, or fraudulent misrepresentation.
          </p>

          <h3>11. Service Availability</h3>
          <p>
            We aim to provide reliable service but cannot guarantee uninterrupted availability. We may suspend 
            the Service for maintenance, updates, or legal compliance requirements.
          </p>

          <h3>12. Termination</h3>
          <p>
            You may terminate your account at any time. We may suspend or terminate accounts for Terms violations 
            or illegal activities. Upon termination, your right to use the Service ceases immediately.
          </p>

          <h3>13. Changes to Terms</h3>
          <p>
            We may update these Terms periodically. Significant changes will be notified via email or Service 
            notification. Continued use after changes constitutes acceptance of new Terms.
          </p>

          <h3>14. Dispute Resolution</h3>
          <p>
            We encourage resolving disputes through direct contact first. If unresolved, disputes may be 
            referred to mediation or the appropriate UK courts.
          </p>

          <h3>15. Governing Law and Jurisdiction</h3>
          <p>
            These Terms are governed by English law. UK courts have exclusive jurisdiction over any disputes, 
            except where you reside in Scotland or Northern Ireland, in which case local courts may have jurisdiction.
          </p>

          <h3>16. Contact Information</h3>
          <p>
            For questions about these Terms, data protection matters, or to exercise your rights:
          </p>
          <p>
            Email: [contact email]<br/>
            Data Protection queries: [dpo email]<br/>
            Address: [UK business address]
          </p>
          
          <h3>17. Severability</h3>
          <p>
            If any provision of these Terms is found unenforceable, the remaining provisions remain in full force and effect.
          </p>
        </div>

        <div className="auth-footer">
          <p>
            <Link to="/">Back to Home</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;